import { Icon, Text, View } from '@/components'
import { Chat } from '@/config'
import { AppIcon, createStyles } from '@codeleap/styles'
import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { LocalMessage } from 'stream-chat'
import { getMessageActions } from '@packages/chat'
import { usePinnedMessage } from '@/hooks'
import { useChannelStateContext, useMessageComposer, useMessageContext } from 'stream-chat-react'
import { Getstream } from '@/services'

export type MessageAction = 'delete' | 'select' | 'threadedReply' | 'copy' | 'star' | 'forward' | 'set-reminder' | 'pin' | 'unpin' | 'edit' | 'quickReply'

export type MessageActionItem = {
  action: MessageAction
  label: string
  icon: AppIcon
  onPress: () => void
  variant?: 'default' | 'danger'
}

export type MessageActionMenuProps = {
  message: LocalMessage
  onDelete?: () => void
  onSelect?: () => void
  onReply?: () => void
  onCopy?: () => void
  onStar?: () => void
  onForward?: () => void
  onSetReminder?: () => void
  onPin?: () => void
  onEdit?: () => void
}

const MessageActionMenuComponent: React.FC<MessageActionMenuProps> = (props) => {
  const { message, onDelete, onReply, onCopy, onForward, onSetReminder, onEdit } = props
  const [isOpen, setIsOpen] = useState(false)
  const [openUpwards, setOpenUpwards] = useState(false)
  const [openLeftwards, setOpenLeftwards] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const { channel } = useChannelStateContext()
  const messageComposer = useMessageComposer()
  // ThreadHead renders its parent with `initialMessage: true` — used to
  // suppress Threaded reply, since you can't start a thread inside a thread.
  const { initialMessage } = useMessageContext()
  const { isGroup, isAdmin } = Getstream.Chat.useMyMembership(channel)
  const { pinnedMessage } = usePinnedMessage(channel?.id, channel?.type)

  const chatUser = Chat.Store.useUser()
  const isMyMessage = message?.user?.id === chatUser?.id
  const isPinned = pinnedMessage?.id === message?.id

  const messageActions = useMemo(() => {
    return getMessageActions({
      message,
      isMyMessage,
      isChatAdmin: isAdmin,
      isMessagePinned: isPinned,
      isChatGroup: isGroup,
      isThreadParent: !!initialMessage,
    })
  }, [isMyMessage, isPinned, isAdmin, isGroup, pinnedMessage?.id, message, initialMessage])

  const handleAction = useCallback(async (actionId: string) => {
    switch (actionId) {
      case 'threadedReply':
        onReply?.()
        break
      case 'forward':
        onForward?.()
        break
      case 'reminder':
        onSetReminder?.()
        break
      case 'copy':
        onCopy?.()
        break
      case 'edit':
        onEdit?.()
        break
      case 'delete':
        onDelete?.()
        break
      // @spec FR-0077/AC-1@v1 — hover-menu trigger sets the composer's quoted message.
      // @spec FR-0077/AC-11@v1 — keyboard activation runs through the same handler.
      case 'quickReply': {
        Chat.Store.setQuotedMessage(message as any)
        // Mirror into Stream's composer so submits via Enter (handled by the
        // textarea, not our Send button) include `quoted_message_id` —
        // otherwise Stream ships the message as a plain reply because the
        // custom store is invisible to its native submit path.
        try {
          messageComposer?.setQuotedMessage?.(message as any)
        } catch {
          // Stream API change is harmless here; the custom store + the Send
          // button still cover the quoted path.
        }
        // Defer focus to the next paint so the preview bar mounts (and
        // layouts) before we ask the textarea to take focus — otherwise the
        // layout shift drops the focus. Two rAFs cover React's commit + the
        // browser's layout pass, matching the timing pattern used by
        // QuotedMessagePreview's highlightAfterRender.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const textarea = document.querySelector(
            '.str-chat__textarea__textarea',
          ) as HTMLTextAreaElement | null
          textarea?.focus()
        }))
        break
      }
      case 'pin':
      case 'unpin':
        const messageListWrapper = document.querySelector('.str-chat__list') as HTMLElement
        const scrollPosition = messageListWrapper?.scrollTop
        let isScrollLocked = true

        const preventScroll = () => {
          if (isScrollLocked && messageListWrapper) {
            messageListWrapper.scrollTop = scrollPosition
          }
        }

        if (messageListWrapper) {
          messageListWrapper.addEventListener('scroll', preventScroll)
        }

        await Chat.Services.togglePinMessage({
          channel,
          message,
        })

        setTimeout(() => {
          isScrollLocked = false
          if (messageListWrapper) {
            messageListWrapper.removeEventListener('scroll', preventScroll)
          }
        }, 500)
        break
      default:
        break
    }
    setIsOpen(false)
    Chat.Store.setIsMessageActionMenuOpen(false)
  }, [onReply, onForward, onSetReminder, onCopy, onEdit, onDelete, channel, message])

  const actions: MessageActionItem[] = messageActions.map(action => ({
    action: action.id as MessageAction,
    label: action.text,
    icon: action.rightIcon as AppIcon,
    onPress: () => handleAction(action.id),
    variant: Array.isArray(action.style) && action.style.includes('list:destructive') ? 'danger' : 'default',
  }))

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        Chat.Store.setIsMessageActionMenuOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside, true)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [isOpen])

  const toggleMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()

    if (buttonRef.current && !isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect()

      const spaceBelow = window.innerHeight - buttonRect.bottom
      const menuHeight = 350
      setOpenUpwards(spaceBelow < menuHeight)

      const spaceRight = window.innerWidth - buttonRect.right
      const menuWidth = 180
      setOpenLeftwards(spaceRight < menuWidth)
    }

    const newIsOpen = !isOpen
    setIsOpen(newIsOpen)
    Chat.Store.setIsMessageActionMenuOpen(newIsOpen)
  }, [isOpen])

  return (
    <View
      className='message-action-menu-wrapper'
      onClick={(e) => e.stopPropagation()}
      style={{
        ...styles.wrapper,
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? 'visible' : 'hidden',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      <View style={styles.container}>
        <View
          ref={buttonRef}
          onClick={toggleMenu}
          style={[
            styles.button,
            isOpen ? styles.buttonOpen : styles.buttonClosed,
          ]}
        >
          <Icon
            name={isOpen ? 'chevron-up' : 'chevron-down'}
            style={styles.buttonIcon}
          />
        </View>

        {isOpen && (
          <View ref={menuRef} style={[
            styles.menu,
            openUpwards ? styles.menuUpwards : styles.menuDownwards,
            openLeftwards ? styles.menuLeftwards : styles.menuRightwards,
          ]}>
            {actions.map((item, index) => (
              <React.Fragment key={item.action}>
                {index > 0 && (
                  <View style={styles.separator} />
                )}
                <View
                  debugName={`message-action:${item.action}`}
                  style={[
                    styles.menuItem,
                    item.variant === 'danger' && styles.menuItemDanger,
                  ]}
                  onClick={(e) => {
                    e.stopPropagation()
                    item.onPress()
                  }}
                >
                  <Text
                    text={item.label}
                    style={[
                      'medium',
                      styles.menuItemText,
                      item.variant === 'danger' && styles.menuItemTextDanger,
                    ]}
                  />
                  <Icon
                    name={item.icon}
                    style={[
                      styles.menuItemIcon,
                      item.variant === 'danger' && styles.menuItemIconDanger,
                    ]}
                  />
                </View>
              </React.Fragment>
            ))}
          </View>
        )}
      </View>
    </View>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    display: 'flex',
    alignItems: 'center',
    transition: 'opacity 0.2s ease, visibility 0.2s ease',
    zIndex: 10,
    borderRadius: '4px',
    backgroundColor: theme.colors.tagBgHover,
  },
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  button: {
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease',
  },
  buttonOpen: {
    backgroundColor: theme.colors.tagBgHover,
  },
  buttonClosed: {
    backgroundColor: 'transparent',
  },
  buttonIcon: {
    fontSize: 20,
    color: theme.colors.textHeadingRegular,
    width: 14,
    height: 14,
  },
  menu: {
    position: 'absolute',
    backgroundColor: '#fff',
    borderRadius: theme.radius.regular,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    minWidth: 180,
    zIndex: 1000,
    overflow: 'hidden',
    border: `1px solid ${'neutralBlackTransparent100'}`,
    display: 'flex',
    flexDirection: 'column',
  },
  menuUpwards: {
    bottom: '100%',
    marginBottom: theme.spacing.value(0.5),
  },
  menuDownwards: {
    top: '100%',
    marginTop: theme.spacing.value(0.5),
  },
  menuLeftwards: {
    right: 0,
    left: 'auto',
  },
  menuRightwards: {
    left: 0,
    right: 'auto',
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.value(1),
    padding: `${theme.spacing.value(1.5)}px ${theme.spacing.value(2)}px`,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',

    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
  menuItemText: {
    fontSize: 14,
    color: theme.colors.textHeadingRegular,
    whiteSpace: 'nowrap',
  },
  menuItemTextDanger: {
    color: theme.colors.buttonNegativeQuaternaryLabelDefault,
  },
  menuItemIcon: {
    ...theme.sized(2.5),
    color: theme.colors.textHeadingRegular,
  },
  menuItemIconDanger: {
    color: theme.colors.buttonNegativeQuaternaryIconDefault,
  },
  separator: {
    width: '100%',
    height: 1,
    backgroundColor: theme.baseColors.neutralBlackTransparent100,
  },
  menuItemDanger: {
    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
}))

export const MessageActionMenu = memo(
  MessageActionMenuComponent,
  (prevProps, nextProps) => {
    const sameId = prevProps.message.id === nextProps.message.id
    const samePinned = prevProps.message.pinned === nextProps.message.pinned
    const sameText = prevProps.message.text === nextProps.message.text
    const sameUserId = prevProps.message?.user?.id === nextProps.message?.user?.id
    return sameId && samePinned && sameText && sameUserId
  },
)
